import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { api } from '../../lib/api';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (n: number) => '₱' + n.toLocaleString(undefined, { minimumFractionDigits: 0 });
const formatDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const stageColor: Record<string, string> = {
  INQUIRY: '#F59E0B', RESERVATION: '#3B82F6', CONTRACT_SIGNING: '#8B5CF6',
  PAYMENT: '#0D9488', COMPLETED: '#10B981', CANCELLED: '#EF4444',
};
const paymentStatusColor: Record<string, string> = {
  PAID: '#10B981', PENDING: '#F59E0B', PARTIAL: '#3B82F6', OVERDUE: '#EF4444', CANCELLED: '#94A3B8',
};

interface PortalAppProps {
  user: any;
  onLogout: () => void;
}

// ─── Portal Layout ──────────────────────────────────────────
const PortalApp: React.FC<PortalAppProps> = ({ user, onLogout }) => {
  const location = useLocation();
  const [mobileNav, setMobileNav] = useState(false);

  const navItems = [
    { path: '/portal', label: 'Dashboard', icon: 'solar:home-2-bold-duotone', exact: true },
    { path: '/portal/purchases', label: 'My Purchases', icon: 'solar:bag-bold-duotone' },
    { path: '/portal/payments', label: 'Payments', icon: 'solar:wallet-money-bold-duotone' },
    { path: '/portal/documents', label: 'Documents', icon: 'solar:document-text-bold-duotone' },
    { path: '/portal/profile', label: 'My Profile', icon: 'solar:user-bold-duotone' },
  ];

  const isActive = (path: string, exact?: boolean) => exact ? location.pathname === path : location.pathname.startsWith(path);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8FAFC' }}>
      {/* Sidebar */}
      <aside style={{
        width: '260px', background: 'white', borderRight: '1px solid #E2E8F0',
        display: 'flex', flexDirection: 'column', flexShrink: 0,
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
        transform: mobileNav ? 'translateX(0)' : undefined,
      }}>
        <div style={{ padding: '24px 20px 16px', borderBottom: '1px solid #F1F5F9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src="/voplogo.png" alt="VOP" width={40} height={40} style={{ borderRadius: '10px', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#1E293B' }}>Client Portal</div>
              <div style={{ fontSize: '12px', color: '#94A3B8' }}>Valley of Peace</div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: '12px 10px' }}>
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path} end={item.exact} onClick={() => setMobileNav(false)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px',
                borderRadius: '10px', textDecoration: 'none', marginBottom: '2px',
                fontSize: '14px', fontWeight: 500, transition: 'all 0.15s',
                color: isActive(item.path, item.exact) ? '#0D9488' : '#64748B',
                background: isActive(item.path, item.exact) ? 'rgba(13,148,136,0.08)' : 'transparent',
              }}>
              <Icon icon={item.icon} width={20} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '16px 10px', borderTop: '1px solid #F1F5F9' }}>
          <div style={{ padding: '10px 14px', marginBottom: '8px' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#1E293B' }}>
              {user.firstName} {user.lastName}
            </div>
            <div style={{ fontSize: '12px', color: '#94A3B8' }}>{user.email}</div>
          </div>
          <button onClick={onLogout} style={{
            display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
            padding: '10px 14px', border: 'none', borderRadius: '10px', cursor: 'pointer',
            background: 'transparent', color: '#EF4444', fontSize: '14px', fontWeight: 500,
            fontFamily: 'inherit', textAlign: 'left',
          }}>
            <Icon icon="solar:logout-2-bold-duotone" width={20} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main style={{ flex: 1, marginLeft: '260px', minHeight: '100vh' }}>
        <Routes>
          <Route path="/" element={<PortalDashboard user={user} />} />
          <Route path="/purchases" element={<PortalPurchases />} />
          <Route path="/purchases/:id" element={<PortalPurchaseDetail />} />
          <Route path="/payments" element={<PortalPayments />} />
          <Route path="/documents" element={<PortalDocuments />} />
          <Route path="/profile" element={<PortalProfile user={user} onLogout={onLogout} />} />
        </Routes>
      </main>
    </div>
  );
};

// ─── Dashboard ──────────────────────────────────────────────
const PortalDashboard: React.FC<{ user: any }> = ({ user }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.portal.dashboard().then(setData).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div style={{ padding: '32px' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: 700, color: '#1E293B' }}>
        Welcome back, {data?.clientName || user.firstName}!
      </h1>
      <p style={{ margin: '0 0 28px', color: '#64748B', fontSize: '15px' }}>Here's a summary of your account.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '28px' }}>
        {[
          { label: 'Total Invested', value: fmt(data?.totalInvested || 0), icon: 'solar:hand-money-bold-duotone', color: '#0D9488' },
          { label: 'Total Paid', value: fmt(data?.totalPaid || 0), icon: 'solar:wallet-money-bold-duotone', color: '#10B981' },
          { label: 'Balance Due', value: fmt(data?.totalBalance || 0), icon: 'solar:bill-list-bold-duotone', color: data?.totalBalance > 0 ? '#EF4444' : '#10B981' },
          { label: 'Purchases', value: String(data?.totalPurchases || 0), icon: 'solar:bag-bold-duotone', color: '#3B82F6' },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'white', borderRadius: '14px', padding: '20px',
            border: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '16px',
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              background: `${s.color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon icon={s.icon} width={24} style={{ color: s.color }} />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#1E293B' }}>{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {((data?.overduePayments || 0) > 0) && (
        <div style={{
          background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px',
          padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px',
        }}>
          <Icon icon="solar:danger-triangle-bold" width={24} style={{ color: '#EF4444' }} />
          <div>
            <div style={{ fontWeight: 600, color: '#DC2626', fontSize: '14px' }}>
              {data.overduePayments} overdue payment{data.overduePayments > 1 ? 's' : ''} — {fmt(data.overdueAmount)}
            </div>
            <div style={{ color: '#B91C1C', fontSize: '13px' }}>Please settle your overdue payments to avoid penalties.</div>
          </div>
        </div>
      )}

      {/* Recent Purchases */}
      {data?.recentPurchases?.length > 0 && (
        <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E2E8F0', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>Recent Purchases</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {data.recentPurchases.map((p: any) => (
              <div key={p.id} style={{
                display: 'flex', alignItems: 'center', gap: '14px', padding: '14px',
                background: '#F8FAFC', borderRadius: '10px',
              }}>
                <Icon icon="solar:map-point-bold-duotone" width={22} style={{ color: '#0D9488' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#1E293B', fontSize: '14px' }}>{p.lot}</div>
                  <div style={{ fontSize: '12px', color: '#94A3B8' }}>{p.type} · {fmt(p.amount)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: '6px', fontSize: '11px',
                    fontWeight: 600, background: `${stageColor[p.stage] || '#94A3B8'}18`,
                    color: stageColor[p.stage] || '#94A3B8',
                  }}>{p.stage.replace('_', ' ')}</span>
                  <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '4px' }}>
                    Paid: {fmt(p.paid)} / {fmt(p.amount)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Purchases ──────────────────────────────────────────────
const PortalPurchases: React.FC = () => {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.portal.purchases().then(setPurchases).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div style={{ padding: '32px' }}>
      <h1 style={{ margin: '0 0 24px', fontSize: '24px', fontWeight: 700, color: '#1E293B' }}>My Purchases</h1>

      {purchases.length === 0 ? (
        <EmptyState icon="solar:bag-bold-duotone" text="No purchases yet." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {purchases.map((sale) => (
            <div key={sale.id} onClick={() => navigate(`/portal/purchases/${sale.id}`)} style={{
              background: 'white', borderRadius: '14px', border: '1px solid #E2E8F0',
              padding: '20px', cursor: 'pointer', transition: 'box-shadow 0.15s',
            }}
            onMouseOver={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)')}
            onMouseOut={e => (e.currentTarget.style.boxShadow = 'none')}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{
                  width: '48px', height: '48px', borderRadius: '12px',
                  background: 'rgba(13,148,136,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon icon="solar:map-point-bold-duotone" width={24} style={{ color: '#0D9488' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '15px' }}>
                    Lot {sale.lot.lotNumber} — {sale.lot.section?.label || ''}
                  </div>
                  <div style={{ fontSize: '13px', color: '#94A3B8', marginTop: '2px' }}>
                    {sale.lot.lotType?.name || ''} · {sale.paymentType} · {formatDate(sale.createdAt)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    display: 'inline-block', padding: '4px 12px', borderRadius: '6px', fontSize: '12px',
                    fontWeight: 600, background: `${stageColor[sale.stage] || '#94A3B8'}14`,
                    color: stageColor[sale.stage] || '#94A3B8',
                  }}>{sale.stage.replace('_', ' ')}</span>
                  <div style={{ fontWeight: 700, color: '#1E293B', fontSize: '16px', marginTop: '6px' }}>
                    {fmt(sale.saleAmount)}
                  </div>
                  <div style={{ fontSize: '12px', color: sale.balance > 0 ? '#EF4444' : '#10B981' }}>
                    Balance: {fmt(sale.balance)}
                  </div>
                </div>
                <Icon icon="solar:arrow-right-bold" width={18} style={{ color: '#CBD5E1' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Purchase Detail ────────────────────────────────────────
const PortalPurchaseDetail: React.FC = () => {
  const navigate = useNavigate();
  const saleId = useLocation().pathname.split('/').pop() || '';
  const [sale, setSale] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (saleId) api.portal.purchase(saleId).then(setSale).catch(console.error).finally(() => setLoading(false));
  }, [saleId]);

  if (loading) return <PageLoader />;
  if (!sale) return <EmptyState icon="solar:close-circle-bold-duotone" text="Purchase not found." />;

  return (
    <div style={{ padding: '32px' }}>
      <button onClick={() => navigate('/portal/purchases')} style={{
        display: 'flex', alignItems: 'center', gap: '6px', border: 'none', background: 'none',
        cursor: 'pointer', color: '#0D9488', fontWeight: 600, fontSize: '14px', fontFamily: 'inherit',
        padding: 0, marginBottom: '20px',
      }}>
        <Icon icon="solar:arrow-left-bold" width={16} /> Back to Purchases
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1E293B' }}>
          Lot {sale.lot.lotNumber} — {sale.lot.section?.label}
        </h1>
        <span style={{
          padding: '4px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
          background: `${stageColor[sale.stage]}14`, color: stageColor[sale.stage],
        }}>{sale.stage.replace('_', ' ')}</span>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Sale Amount', value: fmt(sale.saleAmount), color: '#1E293B' },
          { label: 'Total Paid', value: fmt(sale.totalPaid), color: '#10B981' },
          { label: 'Balance', value: fmt(sale.balance), color: sale.balance > 0 ? '#EF4444' : '#10B981' },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'white', borderRadius: '12px', padding: '18px', border: '1px solid #E2E8F0', textAlign: 'center',
          }}>
            <div style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: s.color, marginTop: '4px' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Details */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #E2E8F0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700, color: '#1E293B' }}>Purchase Details</h3>
          {[
            ['Payment Type', sale.paymentType],
            ['Down Payment', fmt(sale.downPayment)],
            ['Installment Months', sale.installmentMonths || '—'],
            ['Contract Date', formatDate(sale.contractDate)],
            ['Purchase Date', formatDate(sale.createdAt)],
          ].map(([k, v], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1F5F9' }}>
              <span style={{ color: '#64748B', fontSize: '13px' }}>{k}</span>
              <span style={{ color: '#1E293B', fontSize: '13px', fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #E2E8F0' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700, color: '#1E293B' }}>Lot & Agent</h3>
          {[
            ['Lot', `${sale.lot.lotNumber} (${sale.lot.lotType?.name || '—'})`],
            ['Section', sale.lot.section?.label || '—'],
            ['Agent', sale.agent?.name || '—'],
            ['Agent Phone', sale.agent?.phone || '—'],
            ['Agent Email', sale.agent?.email || '—'],
          ].map(([k, v], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1F5F9' }}>
              <span style={{ color: '#64748B', fontSize: '13px' }}>{k}</span>
              <span style={{ color: '#1E293B', fontSize: '13px', fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Schedule */}
      {sale.payments?.length > 0 && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #E2E8F0' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700, color: '#1E293B' }}>
            Payment Schedule ({sale.payments.length} payments)
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                {['#', 'Due Date', 'Amount', 'Paid', 'Status', 'Paid Date', 'Method'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748B', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sale.payments.map((p: any, i: number) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '10px' }}>{p.installmentNo || i + 1}</td>
                  <td style={{ padding: '10px' }}>{formatDate(p.dueDate)}</td>
                  <td style={{ padding: '10px', fontWeight: 600 }}>{fmt(p.amount)}</td>
                  <td style={{ padding: '10px', fontWeight: 600, color: p.amountPaid > 0 ? '#10B981' : '#94A3B8' }}>{fmt(p.amountPaid)}</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                      background: `${paymentStatusColor[p.status] || '#94A3B8'}14`,
                      color: paymentStatusColor[p.status] || '#94A3B8',
                    }}>{p.status}</span>
                  </td>
                  <td style={{ padding: '10px', color: '#64748B' }}>{formatDate(p.paidDate)}</td>
                  <td style={{ padding: '10px', color: '#64748B' }}>{p.paymentMethod || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Payments ───────────────────────────────────────────────
const PortalPayments: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const params: Record<string, string> = {};
    if (filter) params.status = filter;
    api.portal.payments(Object.keys(params).length ? params : undefined).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [filter]);

  if (loading) return <PageLoader />;

  const summary = data?.summary || {};
  const payments = data?.payments || [];

  return (
    <div style={{ padding: '32px' }}>
      <h1 style={{ margin: '0 0 24px', fontSize: '24px', fontWeight: 700, color: '#1E293B' }}>Payments</h1>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '24px' }}>
        {[
          { label: 'Total Paid', value: fmt(summary.totalPaid || 0), color: '#10B981' },
          { label: 'Pending', value: `${summary.pendingCount || 0} (${fmt(summary.totalPending || 0)})`, color: '#F59E0B' },
          { label: 'Overdue', value: `${summary.overdueCount || 0} (${fmt(summary.totalOverdue || 0)})`, color: '#EF4444' },
          { label: 'Completed', value: String(summary.paidCount || 0), color: '#10B981' },
        ].map((s, i) => (
          <div key={i} style={{
            background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #E2E8F0', textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: s.color, marginTop: '4px' }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {['', 'PENDING', 'PAID', 'OVERDUE', 'PARTIAL'].map(f => (
          <button key={f} onClick={() => { setFilter(f); setLoading(true); }} style={{
            padding: '6px 14px', borderRadius: '8px', border: '1px solid #E2E8F0', cursor: 'pointer',
            background: filter === f ? '#0D9488' : 'white', color: filter === f ? 'white' : '#64748B',
            fontSize: '13px', fontWeight: 500, fontFamily: 'inherit',
          }}>{f || 'All'}</button>
        ))}
      </div>

      {/* Table */}
      {payments.length === 0 ? (
        <EmptyState icon="solar:wallet-money-bold-duotone" text="No payments found." />
      ) : (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC' }}>
                {['Lot', 'Due Date', 'Amount', 'Paid', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '12px 14px', color: '#64748B', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p: any) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ fontWeight: 600, color: '#1E293B' }}>Lot {p.sale?.lot?.lotNumber}</div>
                    <div style={{ fontSize: '11px', color: '#94A3B8' }}>{p.sale?.lot?.section?.label}</div>
                  </td>
                  <td style={{ padding: '12px 14px', color: '#475569' }}>{formatDate(p.dueDate)}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1E293B' }}>{fmt(p.amount)}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: p.amountPaid > 0 ? '#10B981' : '#94A3B8' }}>{fmt(p.amountPaid)}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                      background: `${paymentStatusColor[p.status]}14`, color: paymentStatusColor[p.status],
                    }}>{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Documents ──────────────────────────────────────────────
const PortalDocuments: React.FC = () => {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.portal.documents().then(setDocs).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;

  const typeIcon: Record<string, string> = {
    contract: 'solar:document-bold-duotone',
    soa: 'solar:clipboard-list-bold-duotone',
    receipt: 'solar:receipt-bold-duotone',
  };
  const typeColor: Record<string, string> = {
    contract: '#8B5CF6', soa: '#3B82F6', receipt: '#10B981',
  };
  const typeLabel: Record<string, string> = {
    contract: 'Contract', soa: 'Statement of Account', receipt: 'Receipt',
  };

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  return (
    <div style={{ padding: '32px' }}>
      <h1 style={{ margin: '0 0 24px', fontSize: '24px', fontWeight: 700, color: '#1E293B' }}>Documents</h1>

      {docs.length === 0 ? (
        <EmptyState icon="solar:document-text-bold-duotone" text="No documents available yet." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {docs.map((doc) => (
            <div key={doc.id} style={{
              background: 'white', borderRadius: '12px', border: '1px solid #E2E8F0',
              padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px',
            }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '10px',
                background: `${typeColor[doc.type] || '#94A3B8'}14`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon icon={typeIcon[doc.type] || 'solar:document-bold-duotone'} width={22}
                  style={{ color: typeColor[doc.type] || '#94A3B8' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: '#1E293B', fontSize: '14px' }}>{doc.title}</div>
                <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>
                  <span style={{
                    display: 'inline-block', padding: '1px 8px', borderRadius: '4px', marginRight: '8px',
                    background: `${typeColor[doc.type]}14`, color: typeColor[doc.type], fontSize: '11px', fontWeight: 600,
                  }}>{typeLabel[doc.type]}</span>
                  {formatDate(doc.date)}
                  {doc.amount != null && ` · ${fmt(doc.amount)}`}
                </div>
              </div>
              <a href={`${API_BASE}${doc.downloadUrl}`} target="_blank" rel="noopener noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                borderRadius: '8px', border: '1px solid #E2E8F0', textDecoration: 'none',
                color: '#0D9488', fontWeight: 600, fontSize: '13px', background: 'white',
              }}>
                <Icon icon="solar:download-bold" width={16} />
                Download
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Profile ────────────────────────────────────────────────
const PortalProfile: React.FC<{ user: any; onLogout: () => void }> = ({ user, onLogout }) => {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  // Password change
  const [showPwChange, setShowPwChange] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');

  const fetchProfile = useCallback(() => {
    api.portal.me().then((data) => { setProfile(data); setForm({ phone: data.phone, address: data.address, emergencyName: data.emergencyName, emergencyPhone: data.emergencyPhone }); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.portal.updateProfile(form);
      setMsg('Profile updated.');
      setEditing(false);
      fetchProfile();
    } catch (err: any) {
      setMsg(err.message || 'Failed to update');
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(''), 3000);
    }
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) { setPwError('All fields are required.'); return; }
    if (pwForm.newPw.length < 8) { setPwError('New password must be at least 8 characters.'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError('Passwords do not match.'); return; }
    setPwLoading(true);
    try {
      await api.portal.changePassword(pwForm.current, pwForm.newPw);
      setPwMsg('Password changed successfully.');
      setShowPwChange(false);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err: any) {
      setPwError(err.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
      setTimeout(() => setPwMsg(''), 3000);
    }
  };

  if (loading) return <PageLoader />;

  return (
    <div style={{ padding: '32px', maxWidth: '700px' }}>
      <h1 style={{ margin: '0 0 24px', fontSize: '24px', fontWeight: 700, color: '#1E293B' }}>My Profile</h1>

      {msg && <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', color: '#16A34A', fontSize: '13px' }}>{msg}</div>}
      {pwMsg && <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', color: '#16A34A', fontSize: '13px' }}>{pwMsg}</div>}

      <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E2E8F0', padding: '24px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>Personal Information</h3>
          {!editing ? (
            <button onClick={() => setEditing(true)} style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
              borderRadius: '8px', border: '1px solid #E2E8F0', cursor: 'pointer',
              background: 'white', color: '#0D9488', fontWeight: 600, fontSize: '13px', fontFamily: 'inherit',
            }}>
              <Icon icon="solar:pen-bold" width={14} /> Edit
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setEditing(false)} style={{
                padding: '6px 14px', borderRadius: '8px', border: '1px solid #E2E8F0',
                cursor: 'pointer', background: 'white', color: '#64748B', fontSize: '13px', fontFamily: 'inherit',
              }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: '6px 14px', borderRadius: '8px', border: 'none',
                cursor: 'pointer', background: '#0D9488', color: 'white', fontWeight: 600, fontSize: '13px', fontFamily: 'inherit',
              }}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {[
            { label: 'Name', value: `${profile?.firstName} ${profile?.lastName}`, readOnly: true },
            { label: 'Email', value: profile?.email || '—', readOnly: true },
            { label: 'Phone', key: 'phone', value: form.phone || '' },
            { label: 'Address', key: 'address', value: form.address || '' },
            { label: 'Emergency Contact', key: 'emergencyName', value: form.emergencyName || '' },
            { label: 'Emergency Phone', key: 'emergencyPhone', value: form.emergencyPhone || '' },
          ].map((field, i) => (
            <div key={i}>
              <div style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>{field.label}</div>
              {editing && !field.readOnly ? (
                <input value={field.value} onChange={e => setForm({ ...form, [field.key!]: e.target.value })}
                  style={{
                    width: '100%', padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '8px',
                    fontSize: '14px', color: '#1E293B', fontFamily: 'inherit', background: '#F8FAFC',
                    boxSizing: 'border-box',
                  }} />
              ) : (
                <div style={{ fontSize: '14px', color: '#1E293B', fontWeight: 500 }}>{field.value || '—'}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Change Password */}
      <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #E2E8F0', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showPwChange ? '16px' : '0' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>Security</h3>
          {!showPwChange && (
            <button onClick={() => setShowPwChange(true)} style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
              borderRadius: '8px', border: '1px solid #E2E8F0', cursor: 'pointer',
              background: 'white', color: '#475569', fontWeight: 600, fontSize: '13px', fontFamily: 'inherit',
            }}>
              <Icon icon="solar:lock-bold-duotone" width={14} /> Change Password
            </button>
          )}
        </div>

        {showPwChange && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {pwError && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px', color: '#DC2626', fontSize: '13px' }}>{pwError}</div>}
            {[
              { label: 'Current Password', key: 'current', value: pwForm.current },
              { label: 'New Password', key: 'newPw', value: pwForm.newPw },
              { label: 'Confirm New Password', key: 'confirm', value: pwForm.confirm },
            ].map((f) => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>{f.label}</label>
                <input type="password" value={f.value} onChange={e => setPwForm({ ...pwForm, [f.key]: e.target.value })}
                  style={{
                    width: '100%', padding: '8px 12px', border: '1px solid #E2E8F0', borderRadius: '8px',
                    fontSize: '14px', fontFamily: 'inherit', background: '#F8FAFC', boxSizing: 'border-box',
                  }} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { setShowPwChange(false); setPwError(''); }} style={{
                padding: '8px 16px', borderRadius: '8px', border: '1px solid #E2E8F0',
                cursor: 'pointer', background: 'white', color: '#64748B', fontSize: '13px', fontFamily: 'inherit',
              }}>Cancel</button>
              <button onClick={handleChangePassword} disabled={pwLoading} style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                cursor: 'pointer', background: '#0D9488', color: 'white', fontWeight: 600, fontSize: '13px', fontFamily: 'inherit',
              }}>{pwLoading ? 'Changing...' : 'Update Password'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Shared Components ──────────────────────────────────────
const PageLoader: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: '#94A3B8' }}>
    <Icon icon="solar:refresh-bold-duotone" width={32} style={{ animation: 'spin 1s linear infinite' }} />
  </div>
);

const EmptyState: React.FC<{ icon: string; text: string }> = ({ icon, text }) => (
  <div style={{
    textAlign: 'center', padding: '60px 20px', background: 'white',
    borderRadius: '14px', border: '1px solid #E2E8F0',
  }}>
    <Icon icon={icon} width={48} style={{ color: '#CBD5E1', marginBottom: '12px' }} />
    <p style={{ color: '#94A3B8', fontSize: '15px', margin: 0 }}>{text}</p>
  </div>
);

export default PortalApp;
